import { useEffect, useState } from 'react';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import { Shell } from '../components/Shell';
import { Sidebar } from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { StatsCard } from '../components/StatsCard';
import { TabFilters } from '../components/TabFilters';
import { StatusBadge } from '../components/StatusBadge';
import { DataTable } from '../components/DataTable';
import { InvoiceDetailsModal } from '../components/InvoiceDetailsModal';
import Loader from '../components/Loader';
import { useT } from '../i18n';
import './InvoicesPageNew.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';

// "2026-07" → "Jul 2026"
const monthLabel = (periodKey) => {
  if (!periodKey || !/^\d{4}-\d{2}$/.test(periodKey)) return '';
  const [y, m] = periodKey.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

// Building icon SVG component
const BuildingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 17V5L10 2L17 5V17H11V12H9V17H3ZM5 15H7V13H5V15ZM5 11H7V9H5V11ZM5 7H7V5.5L10 4L13 5.5V7H11V9H13V11H11V13H13V15H15V6.5L10 4.17L5 6.5V7ZM11 9H13V7H11V9ZM11 13H13V11H11V13Z" fill="#1f2937"/>
  </svg>
);

export const InvoicesPageNew = () => {
  const { t } = useT();
  const [invoices, setInvoices] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeScope, setActiveScope] = useState('scope2');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(11);
  const [expandedScope, setExpandedScope] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  // Month-over-month KPI comparison (anchor month vs the month before),
  // computed server-side in /api/dashboard from the real emission records
  const [mom, setMom] = useState(null);

  useEffect(() => {
    fetchInvoices();
    fetchBuildings();
    fetchMonthOverMonth();
  }, []);

  const fetchMonthOverMonth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await fetch(`${API_BASE}/api/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      setMom((data.kpis && data.kpis.monthOverMonth) || null);
    } catch (error) {
      console.error('Error fetching month-over-month KPIs:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        alert(t('invp.sessionExpired'));
        window.location.href = '/';
        return;
      }

      const response = await fetch(`${API_BASE}/api/invoices`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        alert(t('invp.sessionExpired'));
        localStorage.removeItem('token');
        window.location.href = '/';
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch invoices');
      const data = await response.json();
      setInvoices(data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBuildings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/buildings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBuildings(data);
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  };

  // Filter invoices by scope, category and search
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         invoice.period?.toLowerCase().includes(searchQuery.toLowerCase());

    let matchesScope = false;

    if (activeScope === 'all') {
      matchesScope = true;
    } else if (activeScope === 'scope1') {
      matchesScope = invoice.scope === 'scope1';
    } else if (activeScope === 'scope2') {
      matchesScope = invoice.scope === 'scope2';
    } else if (activeScope === 'scope3') {
      matchesScope = invoice.scope === 'scope3';
    } else {
      const categoryMap = {
        'djegja-stacionare': 'Djegja Stacionare',
        'djegja-mobile': 'Djegja Mobile',
        'emetimet': 'Proceset Industriale',
        'elektricitet': 'Energjia Elektrike'
      };

      const targetCategory = categoryMap[activeScope];
      matchesScope = invoice.category === targetCategory;
    }

    return matchesSearch && matchesScope;
  });

  // Calculate statistics
  const totalEmissions = filteredInvoices.reduce((sum, inv) => {
    return sum + parseFloat(inv.emissions_tco2e || 0);
  }, 0);

  const paidInvoices = filteredInvoices.filter(inv => inv.status === 'paid');
  const unpaidInvoices = filteredInvoices.filter(inv => inv.status !== 'paid');

  // Pagination
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInvoices = filteredInvoices.slice(startIndex, startIndex + itemsPerPage);

  // Helper function to get building name from ID
  const getBuildingName = (buildingId) => {
    if (!buildingId) return 'N/A';
    const building = buildings.find(b => b.id === buildingId);
    return building ? building.name : `Building #${buildingId}`;
  };

  // Format date nicely
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const options = {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      };
      return date.toLocaleDateString('en-US', options).replace(',', ' -');
    } catch {
      return dateString;
    }
  };

  // Handle row click
  const handleRowClick = async (row) => {
    try {
      setLoadingDetails(true);
      const token = localStorage.getItem('token');

      if (!token) {
        alert(t('invp.sessionExpired'));
        window.location.href = '/';
        return;
      }

      const response = await fetch(`${API_BASE}/api/invoices/${row.id}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        alert(t('invp.sessionExpired'));
        localStorage.removeItem('token');
        window.location.href = '/';
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch invoice details');
      }

      const data = await response.json();
      setSelectedInvoice(data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      alert(t('invnp.detailsLoadFailed'));
    } finally {
      setLoadingDetails(false);
    }
  };

  // Get scope title for header
  const getScopeTitle = () => {
    if (activeScope === 'all') return t('invnp.allTab');
    if (activeScope === 'scope1') return 'SCOPE 1';
    if (activeScope === 'scope2') return 'SCOPE 2';
    if (activeScope === 'scope3') return 'SCOPE 3';
    return activeScope.toUpperCase();
  };

  // Delete an invoice/calculation (wired to the row "•••" menu)
  const handleDeleteInvoice = async (row) => {
    const label = row.title || row.name || t('invnp.thisInvoice');
    if (!window.confirm(t('invnp.confirmDelete', { label }))) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/invoices/${row.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(t('invnp.deleteFailed'));
      setInvoices(prev => prev.filter(inv => inv.id !== row.id));
      setSelectedRows(prev => prev.filter(id => id !== row.id));
    } catch (err) {
      alert(t('invnp.errorWithMessage', { message: err.message }));
    }
  };

  // Table columns
  const columns = [
    {
      id: 'icon',
      label: '',
      field: 'icon',
      render: () => (
        <div className="invoice-icon">
          <BuildingIcon />
        </div>
      )
    },
    {
      id: 'name',
      label: 'Invoice Name',
      field: 'title',
      sortable: true
    },
    {
      id: 'source',
      label: t('invnp.colSource'),
      field: 'source',
      render: (val) => (
        <span style={{
          display: 'inline-block',
          padding: '3px 10px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 600,
          background: val === 'ocr' ? '#e0fff8' : '#f3f4f6',
          color: val === 'ocr' ? '#0d7d6c' : '#6b7280',
          letterSpacing: '0.3px'
        }}>
          {val === 'ocr' ? t('invnp.scanned') : 'Manual'}
        </span>
      )
    },
    {
      id: 'building',
      label: t('invnp.colBuilding'),
      field: 'building_id',
      sortable: true,
      render: (val) => getBuildingName(val)
    },
    {
      id: 'emissions',
      label: t('invnp.colEmissions'),
      field: 'emissions_tco2e',
      sortable: true,
      render: (val, row) => {
        const emissions = parseFloat(val || row.emissions_tco2e || 0);
        // Display in tonnes (tCO2e)
        return (
          <span>
            <strong>{emissions.toFixed(3)}</strong>
            <span className="emissions-unit"> t CO₂e</span>
          </span>
        );
      }
    },
    {
      id: 'date',
      label: 'Date & Time',
      field: 'created_at',
      sortable: true,
      render: (val) => formatDate(val)
    },
    {
      id: 'status',
      label: 'Status',
      field: 'status',
      render: (val) => <StatusBadge status={val || 'unpaid'} />
    }
  ];

  // Define tab structure
  const tabStructure = [
    {
      id: 'scope1',
      label: 'Scope 1',
      isParent: true,
      children: [
        { id: 'djegja-stacionare', label: t('invnp.tabStationary') },
        { id: 'djegja-mobile', label: t('invnp.tabMobile') },
        { id: 'emetimet', label: t('invnp.tabProcess') }
      ]
    },
    {
      id: 'scope2',
      label: 'Scope 2',
      isParent: true,
      children: [
        { id: 'elektricitet', label: t('invnp.tabElectricity') }
      ]
    },
    {
      id: 'scope3',
      label: 'Scope 3',
      isParent: true,
      children: []
    }
  ];

  // Build visible tabs
  const scopeTabs = [];
  tabStructure.forEach(parent => {
    scopeTabs.push(parent);
    if (expandedScope === parent.id && parent.children.length > 0) {
      parent.children.forEach(child => {
        scopeTabs.push({ ...child, isChild: true });
      });
    }
  });

  // Handle tab click
  const handleTabClick = (tabId) => {
    const clickedTab = tabStructure.find(t => t.id === tabId);

    if (clickedTab && clickedTab.isParent) {
      if (expandedScope === tabId) {
        setActiveScope(tabId);
      } else {
        setExpandedScope(tabId);
        setActiveScope(tabId);
      }
    } else {
      setActiveScope(tabId);
    }
    setCurrentPage(1);
  };

  // Render pagination
  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 3;

    // Always show first page
    pages.push(
      <button
        key={1}
        className={`pagination-btn ${currentPage === 1 ? 'active' : ''}`}
        onClick={() => setCurrentPage(1)}
      >
        1
      </button>
    );

    // Show pages around current page
    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);

    if (startPage > 2) {
      pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
    }

    for (let i = startPage; i <= endPage; i++) {
      if (i !== 1 && i !== totalPages) {
        pages.push(
          <button
            key={i}
            className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
            onClick={() => setCurrentPage(i)}
          >
            {i}
          </button>
        );
      }
    }

    if (endPage < totalPages - 1) {
      pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
    }

    // Always show last page if more than 1 page
    if (totalPages > 1) {
      pages.push(
        <button
          key={totalPages}
          className={`pagination-btn ${currentPage === totalPages ? 'active' : ''}`}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  return (
    <ErrorBoundary>
      <Shell sidebar={<Sidebar />}>
        <Navbar pageTitle={t('invnp.pageTitle')} />
        <div className="invoices-page-new">
          {(loading || loadingDetails) && <Loader />}

          {/* Stats Cards — anchor month vs previous month, from real records */}
          <div className="stats-grid">
            <StatsCard
              title={mom ? `Total Emissions — ${monthLabel(mom.period)}` : 'Total Emissions'}
              value={mom ? mom.total.current.toFixed(3) : totalEmissions.toFixed(3)}
              unit="t CO₂e"
              subtext={mom
                ? `vs ${monthLabel(mom.prevPeriod)}: ${mom.total.prev.toFixed(3)} t CO₂e (${mom.total.diff >= 0 ? '+' : '−'}${Math.abs(mom.total.diff).toFixed(3)} t)`
                : ''}
              change={mom ? `${Math.abs(mom.total.deltaPct).toFixed(2)} %` : null}
              changeType={mom && mom.total.deltaPositive ? 'increase' : 'decrease'}
              sentiment={mom && mom.total.deltaPositive ? 'bad' : 'good'}
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="10" width="4" height="11" rx="1" fill="#3fffd8"/>
                  <rect x="10" y="6" width="4" height="15" rx="1" fill="#3fffd8"/>
                  <rect x="17" y="3" width="4" height="18" rx="1" fill="#3fffd8"/>
                </svg>
              }
            />
            <StatsCard
              title="Average Intensity"
              value={mom && mom.intensity.hasArea ? mom.intensity.current.toFixed(1) : '—'}
              unit="kgCO₂/m²"
              subtext={mom && mom.intensity.hasArea
                ? `vs ${monthLabel(mom.prevPeriod)}: ${mom.intensity.prev.toFixed(1)} kgCO₂/m²`
                : 'Set building area to enable'}
              change={mom && mom.intensity.hasArea ? `${Math.abs(mom.intensity.deltaPct).toFixed(2)} %` : null}
              changeType={mom && mom.intensity.deltaPositive ? 'increase' : 'decrease'}
              sentiment={mom && mom.intensity.deltaPositive ? 'bad' : 'good'}
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="9" stroke="#3fffd8" strokeWidth="2"/>
                  <path d="M12 7V12L15 15" stroke="#3fffd8" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              }
            />
            <StatsCard
              title="Energy Efficiency Ratio"
              value={mom && mom.energyRatio.current !== null ? mom.energyRatio.current.toFixed(2) : '—'}
              unit="kgCO₂/kWh"
              subtext={mom && mom.energyRatio.current !== null
                ? (mom.energyRatio.prev !== null
                    ? `vs ${monthLabel(mom.prevPeriod)}: ${mom.energyRatio.prev.toFixed(2)} kgCO₂/kWh`
                    : `no energy data for ${monthLabel(mom.prevPeriod)}`)
                : 'No energy data for this month'}
              change={mom && mom.energyRatio.deltaPct !== null ? `${Math.abs(mom.energyRatio.deltaPct).toFixed(2)} %` : null}
              changeType={mom && mom.energyRatio.deltaPositive ? 'increase' : 'decrease'}
              sentiment={mom && mom.energyRatio.deltaPositive ? 'bad' : 'good'}
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="9" stroke="#3fffd8" strokeWidth="2"/>
                  <path d="M12 8V16M8 12H16" stroke="#3fffd8" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              }
            />
          </div>

          {/* Filters Section */}
          <div className="filters-section">
            <div className="filters-row">
              <TabFilters
                tabs={scopeTabs}
                activeTab={activeScope}
                onChange={handleTabClick}
                showAllTab={true}
              />
              <div className="search-box">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 12C9.76142 12 12 9.76142 12 7C12 4.23858 9.76142 2 7 2C4.23858 2 2 4.23858 2 7C2 9.76142 4.23858 12 7 12Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 14L10.5 10.5" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search invoice"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Data Table */}
          {!loading && (
            <>
              <DataTable
                columns={columns}
                data={paginatedInvoices}
                showCheckbox={true}
                selectedRows={selectedRows}
                onSelectRow={(id, checked) => {
                  setSelectedRows(prev =>
                    checked ? [...prev, id] : prev.filter(rowId => rowId !== id)
                  );
                }}
                onSelectAll={(checked) => {
                  setSelectedRows(checked ? paginatedInvoices.map(inv => inv.id) : []);
                }}
                onRowClick={handleRowClick}
                onDelete={handleDeleteInvoice}
              />

              {/* Pagination */}
              {totalPages > 0 && (
                <div className="pagination-section">
                  <div className="pagination-info">
                    <span>Showing</span>
                    <select
                      className="items-per-page-select"
                      value={itemsPerPage}
                      disabled
                    >
                      <option value={11}>11</option>
                    </select>
                    <span>out of {filteredInvoices.length}</span>
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn nav-btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(1)}
                    >
                      «
                    </button>
                    <button
                      className="pagination-btn nav-btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    >
                      ‹
                    </button>
                    {renderPagination()}
                    <button
                      className="pagination-btn nav-btn"
                      disabled={currentPage === totalPages || totalPages === 0}
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    >
                      ›
                    </button>
                    <button
                      className="pagination-btn nav-btn"
                      disabled={currentPage === totalPages || totalPages === 0}
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      »
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Invoice Details Modal */}
        {showDetailsModal && (
          <InvoiceDetailsModal
            isOpen={showDetailsModal}
            onClose={() => setShowDetailsModal(false)}
            invoiceData={selectedInvoice}
          />
        )}
      </Shell>
    </ErrorBoundary>
  );
};
